// Audio waveform. Samples time-domain data every animation frame and renders
// vertical bars on a `<canvas>` whose height tracks the audio amplitude. The
// bars use the parent element's `color` (resolved via `getComputedStyle`) so
// they pick up the surrounding Tailwind text color — pass e.g.
// `text-brand-extra-2-40` on a wrapper.
//
// Two amplitude sources are supported:
//   - `stream` + `audioContext`: the visualiser creates its own
//     `AnalyserNode` off a `MediaStreamAudioSourceNode` (the dictation panel
//     and the banner's microphone view). Cheap; keeps the visualiser
//     independent of the PCM downsampler pipeline.
//   - `externalAnalyser`: an analyser owned by the caller (the banner taps one
//     off the assistant-playback pipeline so the bars move while the assistant
//     speaks). When given, the visualiser does NOT create or disconnect it.
//
// `barCount` and `height` are configurable (the banner renders a larger
// waveform); both default to the original dictation-panel sizing.

import { useEffect, useRef } from "react";

interface WaveformVisualizerProps {
  stream?: MediaStream;
  audioContext?: AudioContext;
  /** Caller-owned analyser to read from instead of `stream`/`audioContext`.
   * Not disconnected on cleanup — the caller owns its lifecycle. */
  externalAnalyser?: AnalyserNode;
  barCount?: number;
  height?: number;
  className?: string;
}

const DEFAULT_BAR_COUNT = 24;
const DEFAULT_HEIGHT = 28;
const BAR_WIDTH = 2;
const BAR_GAP = 3;
const MIN_BAR_HEIGHT = 2;
// Time-domain amplitude amplifier. 128 is silence centre; raw peaks rarely
// exceed ~40 from centre for normal speech, so we multiply to fill the
// canvas height. 2.5 picks pleasant peaks without clipping all the time.
const AMPLITUDE_GAIN = 2.5;

export function WaveformVisualizer({
  stream,
  audioContext,
  externalAnalyser,
  barCount = DEFAULT_BAR_COUNT,
  height = DEFAULT_HEIGHT,
  className,
}: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasWidth = barCount * BAR_WIDTH + (barCount - 1) * BAR_GAP;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Resolve the analyser: a caller-owned one, or our own off the stream.
    // `ownsAnalyser` gates teardown so we never disconnect a borrowed node.
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let ownsAnalyser = false;
    if (externalAnalyser) {
      analyser = externalAnalyser;
    } else if (stream && audioContext) {
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      ownsAnalyser = true;
    }
    if (!analyser) return;

    const sampleCount = analyser.fftSize;
    const samples = new Uint8Array(sampleCount);
    // Guard against barCount > fftSize (would divide by zero → NaN bars).
    const samplesPerBar = Math.max(1, Math.floor(sampleCount / barCount));
    let frame = 0;

    const draw = () => {
      analyser.getByteTimeDomainData(samples);

      // Use the wrapper's text color so the bars match the surrounding
      // theme without hardcoding hex values.
      const resolvedColor = canvas.parentElement
        ? getComputedStyle(canvas.parentElement).color
        : "currentColor";
      ctx.clearRect(0, 0, canvasWidth, height);
      ctx.fillStyle = resolvedColor;

      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        const start = i * samplesPerBar;
        for (let j = 0; j < samplesPerBar; j++) {
          // noUncheckedIndexedAccess yields `number | undefined`; the
          // index is always in range by construction (start + j stays
          // below sampleCount), so the fallback is defensive only.
          const value = samples[start + j] ?? 128;
          sum += Math.abs(value - 128);
        }
        const avg = sum / samplesPerBar;
        const normalised = Math.min(1, (avg / 128) * AMPLITUDE_GAIN);
        const barHeight = Math.max(MIN_BAR_HEIGHT, normalised * height);
        const x = i * (BAR_WIDTH + BAR_GAP);
        const y = (height - barHeight) / 2;
        ctx.fillRect(x, y, BAR_WIDTH, barHeight);
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      if (ownsAnalyser) {
        source?.disconnect();
        analyser.disconnect();
      }
    };
  }, [stream, audioContext, externalAnalyser, barCount, height, canvasWidth]);

  return (
    <canvas
      ref={canvasRef}
      width={canvasWidth}
      height={height}
      className={className}
      aria-hidden="true"
      tabIndex={-1}
    />
  );
}
